// Admin presale settings. The primary action is "Sale size" — one input that
// captures intent ("I want to sell N tokens") and orchestrates whatever
// underlying transactions are needed (token transfer into the Sale contract
// + setSaleAllocation). Price and Treasury are separate single-tx rows.

import React, { useEffect, useRef, useState } from 'react';
import {
  useAccount,
  useChainId,
  usePublicClient,
  useWalletClient,
  useReadContracts,
  useWriteContract,
  useSwitchChain,
  useWaitForTransactionReceipt,
} from 'wagmi';
import { parseUnits, formatUnits, isAddress } from 'viem';
import { useUnifiedWallet } from '../contexts/UnifiedWalletContext';
import { chainConfig, BSC_CHAIN_ID, USDT_ADDRESS, addressUrl, txUrl } from '../config/chains';
import { useActiveContracts } from '../hooks/useActiveContracts';
import api from '../services/api';
import './PresaleSettings.css';

const SALE_ABI = [
  { type: 'function', name: 'rate', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'saleAllocation', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'sold', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'treasury', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'owner', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'setRate', stateMutability: 'nonpayable', inputs: [{ name: 'newRate', type: 'uint256' }], outputs: [] },
  { type: 'function', name: 'setSaleAllocation', stateMutability: 'nonpayable', inputs: [{ name: 'newAlloc', type: 'uint256' }], outputs: [] },
  { type: 'function', name: 'setTreasury', stateMutability: 'nonpayable', inputs: [{ name: 'newTreasury', type: 'address' }], outputs: [] },
];

const ERC20_ABI = [
  { type: 'function', name: 'balanceOf', stateMutability: 'view', inputs: [{ name: 'account', type: 'address' }], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'transfer', stateMutability: 'nonpayable', inputs: [{ name: 'to', type: 'address' }, { name: 'amount', type: 'uint256' }], outputs: [{ type: 'bool' }] },
];

const SCALE = 10n ** 36n;

// On-chain rate is "tokens per 1 USDT" (1e18-scaled). UI works in
// "USDT per 1 token" (price). They're reciprocals: price = 1e36 / rate.
function rateToPriceWei(rate) {
  if (!rate || rate === 0n) return 0n;
  return SCALE / rate;
}
function priceWeiToRate(priceWei) {
  if (!priceWei || priceWei === 0n) return 0n;
  return SCALE / priceWei;
}

function shortAddr(a) {
  if (!a) return '—';
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function AddressChip({ chain = 'bsc', address, label }) {
  const [copied, setCopied] = useState(false);
  if (!address) {
    return (
      <div className="ps-chip ps-chip-empty">
        <span className="ps-chip-label">{label}</span>
        <span className="ps-chip-value">—</span>
      </div>
    );
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  return (
    <div className="ps-chip">
      <span className="ps-chip-label">{label}</span>
      <a className="ps-chip-value" href={addressUrl(chain, address)} target="_blank" rel="noreferrer" title={address}>
        {shortAddr(address)}
      </a>
      <button type="button" className="ps-chip-copy" onClick={copy} title="Copy">
        {copied ? '✓' : '⧉'}
      </button>
    </div>
  );
}

function Pending({ hash }) {
  const { isLoading, isSuccess, isError } = useWaitForTransactionReceipt({ hash });
  if (!hash) return null;
  if (isLoading) {
    return (
      <a className="ps-status ps-status-pending" href={txUrl('bsc', hash)} target="_blank" rel="noreferrer">
        <span className="ps-dot" /> Submitted · mining…
      </a>
    );
  }
  if (isSuccess) {
    return (
      <a className="ps-status ps-status-ok" href={txUrl('bsc', hash)} target="_blank" rel="noreferrer">
        ✓ Confirmed on-chain
      </a>
    );
  }
  if (isError) {
    return (
      <a className="ps-status ps-status-err" href={txUrl('bsc', hash)} target="_blank" rel="noreferrer">
        ✗ Transaction failed
      </a>
    );
  }
  return null;
}

function PresaleSettings() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const { switchChain } = useSwitchChain();
  const { connectWallet } = useUnifiedWallet();
  const {
    tokenAddress: TOKEN_ADDRESS,
    saleAddress: SALE_ADDRESS,
    loading: contractsLoading,
    refetch: refetchContracts,
  } = useActiveContracts();

  const wrongChain = isConnected && chainId !== BSC_CHAIN_ID;
  const bsc = chainConfig('bsc');

  const reads = useReadContracts({
    contracts: SALE_ADDRESS
      ? [
          { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'rate' },
          { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'saleAllocation' },
          { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'sold' },
          { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'treasury' },
          { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'owner' },
        ]
      : [],
    query: { enabled: !!SALE_ADDRESS, refetchInterval: 15000 },
  });

  const balances = useReadContracts({
    contracts: TOKEN_ADDRESS && SALE_ADDRESS
      ? [
          { address: TOKEN_ADDRESS, abi: ERC20_ABI, functionName: 'balanceOf', args: [SALE_ADDRESS] },
          ...(address ? [{ address: TOKEN_ADDRESS, abi: ERC20_ABI, functionName: 'balanceOf', args: [address] }] : []),
        ]
      : [],
    query: { enabled: !!(TOKEN_ADDRESS && SALE_ADDRESS), refetchInterval: 15000 },
  });

  const [rateHash, setRateHash] = useState(undefined);
  const [treasuryHash, setTreasuryHash] = useState(undefined);

  const writeRate = useWriteContract();
  const writeTreasury = useWriteContract();
  // For the combined "Sale size" flow we want to chain two txs and await
  // receipts between them, so we use the async write hooks.
  const writeTransfer = useWriteContract();
  const writeAlloc = useWriteContract();

  const [priceInput, setPriceInput] = useState('');
  const [sizeInput, setSizeInput] = useState('');
  const [treasuryInput, setTreasuryInput] = useState('');
  // Drives the Sale-size two-step flow's progress UI.
  const [sizeFlow, setSizeFlow] = useState(null);

  if (contractsLoading) {
    return (
      <div className="ps-page">
        <div className="ps-shell">
          <header className="ps-header">
            <div>
              <div className="ps-eyebrow">Admin · {bsc.name}</div>
              <h1 className="ps-title">Presale Settings</h1>
            </div>
          </header>
          <div className="ps-skeleton">Loading on-chain values…</div>
        </div>
      </div>
    );
  }

  if (!SALE_ADDRESS) {
    return (
      <div className="ps-page">
        <div className="ps-shell">
          <header className="ps-header">
            <div>
              <div className="ps-eyebrow">Admin · {bsc.name}</div>
              <h1 className="ps-title">Deploy Sale Contract</h1>
              <p className="ps-subtitle">No Sale contract on record yet. Deploy <code>Sale.sol</code> from your wallet to begin.</p>
            </div>
          </header>
          <DeploySale
            tokenAddress={TOKEN_ADDRESS}
            onDeployed={refetchContracts}
            isConnected={isConnected}
            wrongChain={wrongChain}
            chainId={chainId}
            bscName={bsc.name}
            connectWallet={connectWallet}
            switchChain={switchChain}
          />
        </div>
      </div>
    );
  }

  const [rateRaw, allocRaw, soldRaw, treasuryAddr, ownerAddr] =
    reads.data?.map((r) => r.result) ?? [];

  const [saleBalRaw, adminBalRaw] = balances.data?.map((r) => r.result) ?? [];

  const isOwner = ownerAddr && address && ownerAddr.toLowerCase() === address.toLowerCase();

  const remainingRaw =
    allocRaw !== undefined && soldRaw !== undefined && allocRaw > soldRaw
      ? allocRaw - soldRaw
      : 0n;
  const shortfallRaw =
    saleBalRaw !== undefined && remainingRaw > saleBalRaw
      ? remainingRaw - saleBalRaw
      : 0n;
  const underfunded = shortfallRaw > 0n;
  const saleBalFmt = saleBalRaw !== undefined ? formatUnits(saleBalRaw, 18) : null;
  const adminBalFmt = adminBalRaw !== undefined ? formatUnits(adminBalRaw, 18) : null;
  const shortfallFmt = shortfallRaw > 0n ? formatUnits(shortfallRaw, 18) : null;

  const priceWei = rateRaw !== undefined ? rateToPriceWei(rateRaw) : undefined;
  const priceFmt = priceWei !== undefined && priceWei !== 0n ? formatUnits(priceWei, 18) : null;
  const allocFmt = allocRaw !== undefined ? formatUnits(allocRaw, 18) : null;
  const soldFmt = soldRaw !== undefined ? formatUnits(soldRaw, 18) : null;
  const soldPct =
    allocRaw && allocRaw > 0n && soldRaw !== undefined
      ? Math.min(100, Number((soldRaw * 10000n) / allocRaw) / 100)
      : 0;

  const fmtNum = (s) => {
    if (s === null || s === undefined) return '—';
    const n = Number(s);
    if (!Number.isFinite(n)) return s;
    return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  };

  // Validates a target Sale size in token-wei against current on-chain state.
  // Returns a plain-language error or null.
  const sizeError = (target) => {
    if (target === undefined || target === null) return null;
    if (target === 0n) return 'Sale size must be greater than zero.';
    if (soldRaw !== undefined && target < soldRaw) {
      return `Buyers have already received ${fmtNum(soldFmt)} tokens, so sale size can't go below that.`;
    }
    if (saleBalRaw !== undefined && soldRaw !== undefined && adminBalRaw !== undefined) {
      const maxPossible = saleBalRaw + soldRaw + adminBalRaw;
      if (target > maxPossible) {
        return `You only hold ${fmtNum(adminBalFmt)} tokens in your wallet — the largest sale size right now is ${fmtNum(formatUnits(maxPossible, 18))}.`;
      }
    }
    return null;
  };

  let parsedSize = null;
  try { parsedSize = sizeInput ? parseUnits(String(sizeInput), 18) : null; } catch {}
  const sizeInputErr = parsedSize !== null ? sizeError(parsedSize) : null;
  // How many tokens we'd need to transfer into the Sale contract to satisfy
  // the requested target. Zero means just an allocation change.
  const sizeNeedTransfer =
    parsedSize !== null && saleBalRaw !== undefined && soldRaw !== undefined && parsedSize > (saleBalRaw + soldRaw)
      ? parsedSize - saleBalRaw - soldRaw
      : 0n;
  const sizeIsNoop = parsedSize !== null && allocRaw !== undefined && parsedSize === allocRaw;

  const submitRate = (e) => {
    e.preventDefault();
    const pWei = parseUnits(String(priceInput || '0'), 18);
    if (pWei === 0n) return;
    const newRate = priceWeiToRate(pWei);
    writeRate.writeContract(
      { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'setRate', args: [newRate] },
      { onSuccess: setRateHash },
    );
  };

  const submitTreasury = (e) => {
    e.preventDefault();
    if (!isAddress(treasuryInput)) return;
    writeTreasury.writeContract(
      { address: SALE_ADDRESS, abi: SALE_ABI, functionName: 'setTreasury', args: [treasuryInput] },
      { onSuccess: setTreasuryHash },
    );
  };

  const submitSize = async (e) => {
    e.preventDefault();
    if (parsedSize === null) return;
    if (sizeInputErr) return;
    if (sizeIsNoop) {
      setSizeFlow({ phase: 'error', message: 'Sale size is already set to this value.' });
      return;
    }
    setSizeFlow({ phase: 'starting', message: 'Preparing…' });
    try {
      if (sizeNeedTransfer > 0n) {
        setSizeFlow({ phase: 'transfer-sign', message: 'Step 1 of 2 — Confirm the token transfer in your wallet…' });
        const hash1 = await writeTransfer.writeContractAsync({
          address: TOKEN_ADDRESS,
          abi: ERC20_ABI,
          functionName: 'transfer',
          args: [SALE_ADDRESS, sizeNeedTransfer],
        });
        setSizeFlow({ phase: 'transfer-mine', message: 'Step 1 of 2 — Moving tokens to the Sale contract…', txHash: hash1 });
        await publicClient.waitForTransactionReceipt({ hash: hash1 });
        await balances.refetch();
      }
      setSizeFlow({
        phase: 'alloc-sign',
        message: sizeNeedTransfer > 0n
          ? 'Step 2 of 2 — Confirm the sale-size update in your wallet…'
          : 'Confirm the sale-size update in your wallet…',
      });
      const hash2 = await writeAlloc.writeContractAsync({
        address: SALE_ADDRESS,
        abi: SALE_ABI,
        functionName: 'setSaleAllocation',
        args: [parsedSize],
      });
      setSizeFlow({
        phase: 'alloc-mine',
        message: sizeNeedTransfer > 0n ? 'Step 2 of 2 — Updating sale size on-chain…' : 'Updating sale size on-chain…',
        txHash: hash2,
      });
      await publicClient.waitForTransactionReceipt({ hash: hash2 });
      await reads.refetch();
      await balances.refetch();
      setSizeFlow({ phase: 'done', message: '✓ Sale size updated', txHash: hash2 });
      setSizeInput('');
    } catch (err) {
      console.error(err);
      setSizeFlow({
        phase: 'error',
        message: err?.shortMessage || err?.message || 'Transaction failed',
      });
    }
  };

  const writesDisabled = !isConnected || wrongChain || !isOwner;
  // Sale size's first step is a token transfer from the admin wallet — doesn't
  // need Sale ownership, but the second step (setSaleAllocation) does.
  const sizeDisabled = writesDisabled || !TOKEN_ADDRESS || sizeFlow?.phase?.endsWith('-sign') || sizeFlow?.phase?.endsWith('-mine');

  const primaryActionLabel = !allocRaw || allocRaw === 0n ? 'Open sale' : 'Update sale size';

  return (
    <div className="ps-page">
      <div className="ps-shell">
        <header className="ps-header">
          <div>
            <div className="ps-eyebrow">Admin · {bsc.name}</div>
            <h1 className="ps-title">Presale Settings</h1>
            <p className="ps-subtitle">Tune the live Sale contract. Each change is signed from your wallet.</p>
          </div>
          <div className="ps-owner-pill" data-state={isOwner ? 'ok' : isConnected ? 'err' : 'neutral'}>
            {!isConnected && <>Wallet not connected</>}
            {isConnected && isOwner && <>✓ Connected as owner</>}
            {isConnected && !isOwner && (
              <>
                Not contract owner
                {ownerAddr && <span className="ps-owner-sub">owner is {shortAddr(ownerAddr)}</span>}
              </>
            )}
          </div>
        </header>

        {!isConnected && (
          <div className="ps-banner ps-banner-warn">
            <span>Connect your wallet to view live values and make changes.</span>
            <button className="ps-btn ps-btn-ghost" onClick={connectWallet}>Connect wallet</button>
          </div>
        )}
        {isConnected && wrongChain && (
          <div className="ps-banner ps-banner-warn">
            <span>You're on chain {chainId}. Switch to {bsc.name} to write to the Sale contract.</span>
            <button className="ps-btn ps-btn-ghost" onClick={() => switchChain({ chainId: BSC_CHAIN_ID })}>
              Switch to {bsc.name}
            </button>
          </div>
        )}
        {isConnected && !wrongChain && !isOwner && (
          <div className="ps-banner ps-banner-err">
            Connected wallet is not the Sale owner — write transactions will revert on-chain. Switch to the owner wallet to make changes.
          </div>
        )}

        <section className="ps-addresses">
          <AddressChip label="Sale" address={SALE_ADDRESS} />
          <AddressChip label="Token" address={TOKEN_ADDRESS} />
          <AddressChip label="USDT" address={USDT_ADDRESS} />
        </section>

        {underfunded && (
          <div className="ps-banner ps-banner-err">
            Sale size is <strong>{fmtNum(allocFmt)}</strong> but the contract only holds enough for <strong>{fmtNum(formatUnits(saleBalRaw + soldRaw, 18))}</strong>. Buyers will hit "sold out" past that. Use the Sale size row below to top it up.
          </div>
        )}

        <section className="ps-stats">
          <div className="ps-stat ps-stat-progress">
            <div className="ps-stat-label">Sold / Sale size</div>
            <div className="ps-stat-value">
              {fmtNum(soldFmt)} <span className="ps-stat-unit">/ {fmtNum(allocFmt)} tokens</span>
            </div>
            <div className="ps-progress" aria-label="sold progress">
              <div className="ps-progress-fill" style={{ width: `${soldPct}%` }} />
            </div>
            <div className="ps-stat-foot">{soldPct.toFixed(2)}% sold</div>
          </div>
          <div className="ps-stat" data-state={underfunded ? 'warn' : 'ok'}>
            <div className="ps-stat-label">Sale contract balance</div>
            <div className="ps-stat-value">
              {fmtNum(saleBalFmt)} <span className="ps-stat-unit">tokens</span>
            </div>
            <div className="ps-stat-foot">
              {underfunded
                ? `Short by ${fmtNum(shortfallFmt)} vs unsold remainder`
                : allocRaw && allocRaw > 0n
                  ? '✓ Funded for remaining sale'
                  : 'No sale opened yet'}
            </div>
          </div>
          <div className="ps-stat">
            <div className="ps-stat-label">Price</div>
            <div className="ps-stat-value">{fmtNum(priceFmt)}</div>
            <div className="ps-stat-foot">USDT per 1 token</div>
          </div>
          <div className="ps-stat">
            <div className="ps-stat-label">Treasury</div>
            <div className="ps-stat-value ps-stat-value-mono">
              {treasuryAddr ? shortAddr(treasuryAddr) : '—'}
            </div>
            <div className="ps-stat-foot">
              {treasuryAddr ? (
                <a href={addressUrl('bsc', treasuryAddr)} target="_blank" rel="noreferrer">View on BscScan ↗</a>
              ) : '—'}
            </div>
          </div>
        </section>

        <section className="ps-settings">
          <h2 className="ps-section-title">Update parameters</h2>

          <form className="ps-row" onSubmit={submitSize}>
            <div className="ps-row-head">
              <div>
                <div className="ps-row-title">Sale size</div>
                <div className="ps-row-help">
                  Total tokens you want to sell in this presale. We'll move tokens
                  into the Sale contract and update the on-chain limit as needed —
                  you may see one or two wallet popups.
                </div>
              </div>
              <div className="ps-row-current">
                <span className="ps-row-current-label">Currently selling</span>
                <span className="ps-row-current-value">{fmtNum(allocFmt)}</span>
              </div>
            </div>
            <div className="ps-row-control">
              <div className="ps-input-group">
                <input
                  value={sizeInput}
                  onChange={(e) => { setSizeInput(e.target.value); setSizeFlow(null); }}
                  placeholder={allocFmt && allocRaw > 0n ? `e.g. ${fmtNum(allocFmt)}` : 'e.g. 10000000'}
                  inputMode="decimal"
                />
                <span className="ps-input-suffix">tokens</span>
              </div>
              <button
                type="submit"
                className="ps-btn"
                disabled={sizeDisabled || !sizeInput || !!sizeInputErr || sizeIsNoop}
              >
                {sizeFlow && (sizeFlow.phase.endsWith('-sign') || sizeFlow.phase.endsWith('-mine') || sizeFlow.phase === 'starting')
                  ? 'Working…'
                  : primaryActionLabel}
              </button>
            </div>
            <div className="ps-size-footnote">
              Your wallet: <strong>{fmtNum(adminBalFmt)}</strong> tokens · Sale contract holds: <strong>{fmtNum(saleBalFmt)}</strong> tokens
              {parsedSize !== null && !sizeInputErr && !sizeIsNoop && (
                <>
                  {' · '}
                  {sizeNeedTransfer > 0n
                    ? <>Will move <strong>{fmtNum(formatUnits(sizeNeedTransfer, 18))}</strong> tokens, then update the limit (2 popups).</>
                    : <>Just updates the on-chain limit (1 popup).</>}
                </>
              )}
            </div>
            {sizeInputErr && (
              <div className="ps-status ps-status-err">{sizeInputErr}</div>
            )}
            {sizeFlow && (
              <div className={`ps-status ps-status-${sizeFlow.phase === 'done' ? 'ok' : sizeFlow.phase === 'error' ? 'err' : 'pending'}`}>
                {sizeFlow.phase !== 'done' && sizeFlow.phase !== 'error' && <span className="ps-dot" />}
                <span>{sizeFlow.message}</span>
                {sizeFlow.txHash && (
                  <> · <a href={txUrl('bsc', sizeFlow.txHash)} target="_blank" rel="noreferrer">tx ↗</a></>
                )}
              </div>
            )}
          </form>

          <form className="ps-row" onSubmit={submitRate}>
            <div className="ps-row-head">
              <div>
                <div className="ps-row-title">Price per token</div>
                <div className="ps-row-help">How much USDT a buyer pays for 1 token.</div>
              </div>
              <div className="ps-row-current">
                <span className="ps-row-current-label">Current</span>
                <span className="ps-row-current-value">{priceFmt ? `${fmtNum(priceFmt)} USDT` : '—'}</span>
              </div>
            </div>
            <div className="ps-row-control">
              <div className="ps-input-group">
                <input
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder={priceFmt ? `e.g. ${fmtNum(priceFmt)}` : 'e.g. 0.01'}
                  inputMode="decimal"
                />
                <span className="ps-input-suffix">USDT / token</span>
              </div>
              <button type="submit" className="ps-btn" disabled={writesDisabled || writeRate.isPending || !priceInput || Number(priceInput) <= 0}>
                {writeRate.isPending ? 'Confirm in wallet…' : 'Update price'}
              </button>
            </div>
            {priceInput && Number(priceInput) > 0 && (
              <div className="ps-size-footnote">
                Preview: 1 USDT = <strong>{(1 / Number(priceInput)).toLocaleString(undefined, { maximumFractionDigits: 6 })}</strong> tokens
              </div>
            )}
            <Pending hash={rateHash} />
            {writeRate.error && (
              <div className="ps-status ps-status-err">{writeRate.error.shortMessage || writeRate.error.message}</div>
            )}
          </form>

          <form className="ps-row" onSubmit={submitTreasury}>
            <div className="ps-row-head">
              <div>
                <div className="ps-row-title">Treasury</div>
                <div className="ps-row-help">USDT from each purchase is forwarded here. For production we recommend a hardware wallet or multisig.</div>
              </div>
              <div className="ps-row-current">
                <span className="ps-row-current-label">Current</span>
                <span className="ps-row-current-value ps-row-current-value-mono">
                  {treasuryAddr ? shortAddr(treasuryAddr) : '—'}
                </span>
              </div>
            </div>
            <div className="ps-row-control">
              <input
                className="ps-input-full"
                value={treasuryInput}
                onChange={(e) => setTreasuryInput(e.target.value)}
                placeholder={address || '0x…'}
              />
              {address && (
                <button
                  type="button"
                  className="ps-btn ps-btn-ghost"
                  onClick={() => setTreasuryInput(address)}
                  disabled={writeTreasury.isPending}
                  title="Use my connected wallet address"
                >
                  Use my wallet
                </button>
              )}
              <button
                type="submit"
                className="ps-btn"
                disabled={writesDisabled || writeTreasury.isPending || !isAddress(treasuryInput || '')}
              >
                {writeTreasury.isPending ? 'Confirm in wallet…' : 'Update treasury'}
              </button>
            </div>
            {treasuryInput && !isAddress(treasuryInput) && (
              <div className="ps-status ps-status-err">Not a valid address.</div>
            )}
            <Pending hash={treasuryHash} />
            {writeTreasury.error && (
              <div className="ps-status ps-status-err">{writeTreasury.error.shortMessage || writeTreasury.error.message}</div>
            )}
          </form>
        </section>
      </div>
    </div>
  );
}

// Deploy Sale.sol from the admin's MetaMask. Backend serves the artifact +
// constructor args; browser signs. On success we POST the address to the DB —
// no env edits, no rebuild.
function DeploySale({
  tokenAddress, onDeployed, isConnected, wrongChain, chainId, bscName,
  connectWallet, switchChain,
}) {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const [form, setForm] = useState({ price: '', treasury: '' });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  // Track whether the admin has typed in the treasury field — if not, we
  // keep auto-seeding it from the connected wallet on changes; once they edit,
  // we stop overwriting their input.
  const treasuryEditedRef = useRef(false);

  // Pre-fill the treasury with the connected wallet address as a sensible
  // default. The admin can overwrite or clear it before signing.
  useEffect(() => {
    if (!address) return;
    if (treasuryEditedRef.current) return;
    setForm((f) => (f.treasury === address ? f : { ...f, treasury: address }));
  }, [address]);

  const onChange = (k) => (e) => {
    if (k === 'treasury') treasuryEditedRef.current = true;
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const useMyWallet = () => {
    if (!address) return;
    treasuryEditedRef.current = true;
    setForm((f) => ({ ...f, treasury: address }));
  };

  async function handleDeploy(e) {
    e.preventDefault();
    if (!isConnected) { await connectWallet(); return; }
    if (wrongChain) { await switchChain({ chainId: BSC_CHAIN_ID }); return; }
    if (!walletClient || !publicClient) return;
    if (!tokenAddress) {
      setStatus({ phase: 'error', message: 'Deploy the token first — Sale needs a token address.' });
      return;
    }
    if (!isAddress(form.treasury)) {
      setStatus({ phase: 'error', message: 'Treasury must be a valid address.' });
      return;
    }

    const pWei = parseUnits(String(form.price || '0'), 18);
    if (pWei === 0n) {
      setStatus({ phase: 'error', message: 'Price must be greater than zero.' });
      return;
    }

    setBusy(true);
    setStatus({ phase: 'preparing', message: 'Fetching Sale artifact…' });
    try {
      const rateWei = priceWeiToRate(pWei);
      const resp = await api.post('/contracts/deploy', {
        kind: 'sale',
        usdt: USDT_ADDRESS,
        token: tokenAddress,
        rate: rateWei.toString(),
        treasury: form.treasury,
      });
      const { abi, bytecode, constructorArgs } = resp.data.data;

      setStatus({ phase: 'signing', message: 'Confirm the deploy in MetaMask…' });
      const hash = await walletClient.deployContract({ abi, bytecode, args: constructorArgs });
      setStatus({ phase: 'mining', message: 'Deploy submitted. Waiting for confirmation…', txHash: hash });

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      const contractAddress = receipt.contractAddress;

      await api.post('/contracts/save-deployed', {
        contractAddress,
        transactionHash: hash,
        deployerAddress: address,
        contractType: 'presale',
        blockNumber: Number(receipt.blockNumber),
      });

      setStatus({ phase: 'done', message: `Sale deployed: ${contractAddress}`, txHash: hash, contractAddress });
      onDeployed?.();
    } catch (err) {
      console.error(err);
      setStatus({ phase: 'error', message: err?.response?.data?.message || err?.shortMessage || err?.message || 'Deploy failed' });
    } finally {
      setBusy(false);
    }
  }

  const samePriceHint = form.price && Number(form.price) > 0
    ? `1 USDT = ${(1 / Number(form.price)).toLocaleString(undefined, { maximumFractionDigits: 6 })} tokens`
    : null;

  return (
    <div className="ps-deploy">
      {!tokenAddress && (
        <div className="ps-banner ps-banner-warn">
          No token contract on record. Deploy the token from <a href="/deploy">/deploy</a> first.
        </div>
      )}

      {!isConnected && (
        <div className="ps-banner ps-banner-warn">
          <span>Connect your wallet to deploy.</span>
          <button className="ps-btn ps-btn-ghost" onClick={connectWallet}>Connect wallet</button>
        </div>
      )}

      {isConnected && wrongChain && (
        <div className="ps-banner ps-banner-warn">
          <span>You're on chain {chainId}.</span>
          <button className="ps-btn ps-btn-ghost" onClick={() => switchChain({ chainId: BSC_CHAIN_ID })}>
            Switch to {bscName}
          </button>
        </div>
      )}

      <form onSubmit={handleDeploy} className="ps-deploy-form">
        <div className="ps-deploy-grid">
          <label className="ps-field">
            <span className="ps-field-label">USDT address <span className="ps-field-note">(fixed)</span></span>
            <input value={USDT_ADDRESS} readOnly disabled />
          </label>
          <label className="ps-field">
            <span className="ps-field-label">Token address <span className="ps-field-note">(from /deploy)</span></span>
            <input value={tokenAddress || ''} readOnly disabled placeholder="(deploy token first)" />
          </label>
          <label className="ps-field">
            <span className="ps-field-label">Initial price <span className="ps-field-note">USDT per 1 token</span></span>
            <input value={form.price} onChange={onChange('price')} placeholder="e.g. 0.01" required disabled={busy} inputMode="decimal" />
            {samePriceHint && <span className="ps-field-note">{samePriceHint}</span>}
          </label>
          <label className="ps-field">
            <span className="ps-field-label">
              Treasury <span className="ps-field-note">USDT receiver</span>
              {address && (
                <button type="button" className="ps-link-btn" onClick={useMyWallet} disabled={busy}>
                  Use my wallet
                </button>
              )}
            </span>
            <input value={form.treasury} onChange={onChange('treasury')} placeholder="0x…" required disabled={busy} />
            <span className="ps-field-note">
              Defaults to your connected wallet — change it to a multisig or cold wallet if you prefer.
            </span>
          </label>
        </div>
        <button type="submit" className="ps-btn ps-btn-lg" disabled={busy || !isConnected || wrongChain || !tokenAddress}>
          {busy ? 'Working…' : 'Deploy Sale'}
        </button>
      </form>

      {status && (
        <div className={`ps-deploy-status ps-deploy-status-${status.phase}`}>
          <div className="ps-deploy-status-msg">{status.message}</div>
          {status.txHash && (
            <div className="ps-deploy-status-line">
              Tx: <a href={txUrl('bsc', status.txHash)} target="_blank" rel="noreferrer">{status.txHash.slice(0, 10)}…</a>
            </div>
          )}
          {status.contractAddress && (
            <>
              <div className="ps-deploy-status-line">
                Sale: <a href={addressUrl('bsc', status.contractAddress)} target="_blank" rel="noreferrer">{status.contractAddress}</a>
              </div>
              <div className="ps-deploy-next">
                Next: set the Sale size below — we'll move your tokens into the Sale contract and open it for buyers in one go.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PresaleSettings;
